import { useEffect, useState, type CSSProperties } from 'react';
import { cn } from '../../lib/cn';
import { formatExact } from '../../lib/format';

export interface WinBurstProps {
  /** Bump this on every new result — remounts the burst so repeat wins replay. */
  triggerId: number;
  /** Credits won. Nothing renders when this is 0 (a loss). */
  amount: number;
  /** `jackpot` gets a bigger burst and a badge — reserve for triples / big multipliers. */
  tier?: 'win' | 'jackpot';
  className?: string;
}

const PARTICLE_COUNT = { win: 10, jackpot: 18 } as const;

/**
 * The payout celebration shared by all three casino games.
 *
 * A number going up is not exciting by itself — the credits already landed in
 * the header a frame ago. What sells a win is motion happening *here*, at the
 * table, at the moment the player is looking: a burst of sparks and a floating
 * total that rises and fades. Keying the whole thing off `triggerId` means two
 * wins in a row both get the full animation instead of the second one being a
 * no-op because `amount` didn't change.
 */
export function WinBurst({ triggerId, amount, tier = 'win', className }: WinBurstProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (triggerId <= 0 || amount <= 0) return;
    setVisible(true);
    const timer = window.setTimeout(() => setVisible(false), tier === 'jackpot' ? 2200 : 1500);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerId]);

  if (!visible || amount <= 0) return null;

  const particles = Array.from({ length: PARTICLE_COUNT[tier] }, (_, i) => i);

  return (
    <div
      key={triggerId}
      aria-hidden
      className={cn('pointer-events-none absolute inset-0 z-10 flex items-center justify-center', className)}
    >
      {particles.map((i) => {
        const angle = (360 / particles.length) * i + (tier === 'jackpot' ? 0 : 10);
        const distance = tier === 'jackpot' ? 90 + (i % 3) * 18 : 60 + (i % 2) * 14;
        return (
          <span
            key={i}
            className={cn(
              'sg-win-spark absolute h-1.5 w-1.5 rounded-full',
              tier === 'jackpot' ? 'bg-chronos-bright' : 'bg-credit-bright',
            )}
            style={
              {
                '--sg-spark-x': `${Math.cos((angle * Math.PI) / 180) * distance}px`,
                '--sg-spark-y': `${Math.sin((angle * Math.PI) / 180) * distance}px`,
                animationDelay: `${(i % 5) * 22}ms`,
              } as CSSProperties
            }
          />
        );
      })}

      <div className="sg-win-payout flex flex-col items-center gap-1">
        {tier === 'jackpot' ? (
          <span className="font-display text-label font-bold uppercase tracking-[0.16em] text-chronos-bright">
            Volltreffer
          </span>
        ) : null}
        <span
          data-sg-numeric
          className={cn(
            'font-display text-2xl font-bold',
            tier === 'jackpot' ? 'text-chronos-bright' : 'text-credit-bright',
          )}
          style={{
            filter: `drop-shadow(0 0 10px rgb(var(${tier === 'jackpot' ? '--sg-chronos' : '--sg-credit'}) / 0.7))`,
          }}
        >
          +{formatExact(amount)}
        </span>
      </div>
    </div>
  );
}
